/**
 * What somebody is allowed to do inside their own organisation.
 *
 * **Four roles and a table, replacing two roles and a boolean.** The old model
 * had `admin` and `member` and one question — "may this person change things
 * everybody lives with" — applied to whole route groups. It could not express
 * the two distinctions a team actually needs: somebody who should see the work
 * without being able to change it, and somebody who may write a campaign
 * without being able to send it to forty thousand people.
 *
 * - **owner** — everything, and the only role that may act on another owner.
 *   One per organisation, created with it.
 * - **admin** — everything an owner can do except touch an owner. Settings,
 *   domains, credentials, the team.
 * - **member** — does the work. Contacts, segments, templates, campaigns, and
 *   sending. Reads settings and the team; changes neither. Never sees
 *   credentials or API keys.
 * - **viewer** — reads. Writes nothing, sends nothing. For the person who
 *   wants the numbers without the ability to post to the list.
 *
 * This is a different axis from an operator, who runs the platform and is not
 * a customer at all — a separate table, a separate sign-in and a separate
 * application. An organisation owner can do everything inside one organisation
 * and nothing outside it. Keeping them apart is what stops "promote somebody
 * to admin" from ever being a way out of a tenant.
 */
export const ORG_ROLES = ['owner', 'admin', 'member', 'viewer'];
/**
 * The whole authorisation model, as data.
 *
 * A table rather than a chain of `if (role === 'admin')`, because the question
 * is asked by middleware on thirty route groups and by a dozen screens
 * deciding whether to draw a button. Spread across those call sites it would
 * be thirty chances to disagree; here, reading a row tells you everything a
 * role can do, and a missing entry grants nothing.
 */
/**
 * Everything, which is what both managing roles get.
 *
 * **Owner and admin differ in who they may act on, not in what they may
 * touch.** An owner may change or remove another owner; an admin may not. That
 * is a rule about people and it lives in `canActOnMember`, so writing the two
 * resource rows out separately would be two identical blocks whose only
 * purpose was to be kept identical — and the day they drifted, the drift would
 * be an authorisation change nobody meant to make.
 */
const EVERYTHING = {
    contacts: ['read', 'write'],
    segments: ['read', 'write'],
    templates: ['read', 'write'],
    campaigns: ['read', 'write', 'send'],
    domains: ['read', 'write'],
    provider: ['read', 'write'],
    apiKeys: ['read', 'write'],
    team: ['read', 'write'],
    settings: ['read', 'write'],
    integrations: ['read', 'write'],
    support: ['read', 'write'],
    // Assigned by an operator. Nobody inside the organisation writes it, which
    // is why not even this row has more than `read`.
    plan: ['read'],
};
const GRANTS = {
    owner: EVERYTHING,
    admin: EVERYTHING,
    member: {
        contacts: ['read', 'write'],
        segments: ['read', 'write'],
        templates: ['read', 'write'],
        campaigns: ['read', 'write', 'send'],
        // Reads which domains are set up, because a campaign's deliverability
        // depends on it and being unable to see why mail is failing helps nobody.
        domains: ['read'],
        // Nothing at all. The row holds a credential, and "read" on it is how a
        // field name or a provider choice becomes a clue about somebody's account.
        provider: [],
        apiKeys: [],
        team: ['read'],
        settings: ['read'],
        integrations: ['read', 'write'],
        plan: ['read'],
        support: ['read', 'write'],
    },
    viewer: {
        contacts: ['read'],
        segments: ['read'],
        templates: ['read'],
        campaigns: ['read'],
        domains: ['read'],
        provider: [],
        apiKeys: [],
        team: ['read'],
        settings: ['read'],
        integrations: ['read'],
        plan: ['read'],
        /*
         * Writable by a viewer, which is the one place that is deliberate.
         *
         * Somebody who may only look at the product is exactly who needs to be
         * able to say that something is wrong with it. Asking for help is not a
         * change to the organisation, and routing it through `settings` — as this
         * briefly did — made the feature admin-only for the people most likely to
         * be stuck.
         */
        support: ['read', 'write'],
    },
};
/**
 * A stored value the model does not recognise reads as the least powerful role.
 *
 * The column has a check constraint, so this should not happen — but "should
 * not" is not a control, and the safe direction for an unknown authorisation
 * value is the one that grants less. Falling back to `admin` would turn a
 * corrupt row into control of somebody's organisation.
 */
export function roleFrom(value) {
    return value === 'owner' || value === 'admin' || value === 'member' ? value : 'viewer';
}
/** Whether `role` may do `action` to `resource`. The only authorisation question. */
export function may(role, resource, action) {
    return GRANTS[role][resource].includes(action);
}
/** Everything a role may do, for a screen that wants to decide once. */
export function grantsOf(role) {
    return GRANTS[role];
}
/**
 * Whether `actor` may change or remove somebody holding `target`.
 *
 * **Named apart from the operator model's `canActOn` on purpose.** The rule is
 * the same sentence — only an owner may act on an owner — but the axes are
 * not, and this package exports both through one barrel. Two functions called
 * `canActOn` differing only in which role type they take is a wrong import
 * that compiles, which is the failure `operatorRole` already warns about for
 * the types themselves.
 *
 * The rule that keeps an admin from locking out the people above them: only an
 * owner may act on an owner. Written once rather than at each call site,
 * because the call sites are "change a role", "remove" and "revoke an
 * invitation", and three copies is two chances to forget.
 *
 * **Acting on yourself is excluded here and handled by the caller.** Whether
 * you may remove your own account is a different question from whether your
 * role outranks itself, and conflating them is how somebody removes the last
 * owner and locks everyone out.
 */
export function canActOnMember(actor, target) {
    if (!may(actor, 'team', 'write'))
        return false;
    return target === 'owner' ? actor === 'owner' : true;
}
/**
 * Whether `actor` may hand out `granting`. See `canActOnMember` on the name.
 *
 * Nobody grants a role above their own. Without this an admin could invite an
 * owner and then be acted on by it — privilege escalation by one POST.
 */
export function canGrantRole(actor, granting) {
    if (!may(actor, 'team', 'write'))
        return false;
    return granting === 'owner' ? actor === 'owner' : true;
}
//# sourceMappingURL=orgRole.js.map